window.createAuth0Helper = (() => {
  const auth0IsErrorCookieName = 'auth0.is.error';
  const authErrorOrLogoutMsg = 'ss.logout.msg';
  const auth0IsAuthenticatedCookieName = 'auth0.is.authenticated';
  const auth0IsLoginCookieName = 'auth0.is.login';
  const auth0LocalStoragePrefix = '@@auth0spajs@@';

  let auth0CreateClient;
  let auth0;
  let ssOptions;

  const _exception = (e) => {
    console.error(e);
    if (window.Sentry) {
      window.Sentry.captureException(e);
    }
  };

  const _urlIndexOf = (string, urlPart) => {
    return string.toLowerCase().indexOf(urlPart.toLowerCase());
  };

  const _urlReplace = (string, urlPartFind, urlPartReplace) => {
    if (!string || !urlPartFind || !urlPartReplace) {
      return string;
    }
    return string
      .toLowerCase()
      .replace(urlPartFind.toLowerCase(), urlPartReplace.toLowerCase());
  };

  const _isDropin = () => {
    return document.cookie.indexOf('x-imp=') >= 0;
  };

  const _getUserClaim = (claim, claimSet) => {
    if (!claimSet) {
      return null;
    }
    return claimSet[`http://shipstation.com/${claim}`];
  };

  const _isSysAdmin = (user) => {
    const userRoleMask = _getUserClaim('role_set', user);
    if (!userRoleMask) {
      return false;
    }
    // eslint-disable-next-line
    return (2048 & userRoleMask) === 2048;
  };

  const _isLocalDevEnv = () => {
    return window.location.href.match(/^http[s]?:\/\/.*local.*.(shipstation|sslocal)\.com/ig);
  };

  /** *
   * @returns {Promise}
   */
  const _createClient = () => {
    console.debug('Creating auth client...');
    console.debug(`domain: ${ssOptions.domain}`);
    console.debug(`client: ${ssOptions.client_id}`);
    console.debug(`connection: ${ssOptions.connection}`);
    return auth0CreateClient({
      domain: ssOptions.domain,
      client_id: ssOptions.client_id,
      connection: ssOptions.connection,
      audience: 'ss:webapi',
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      disableCaptcha: ssOptions.disableCaptcha,
      auth0LoginConfigToken: ssOptions.auth0LoginConfigToken,
      ...ssOptions.auth0WhiteLabelOptions,
      // start temp auth0 spa debug
      // sentryDsn: ssOptions.sentryDsn,
      // sentryVersion: `spa-auth0-helper-${ssOptions.version}`,
      // end temp auth0 spa debug
    });
  };

  const _isCodeExchange = () => {
    const { search } = window.location;
    return search.includes('code=') && search.includes('state=');
  };

  const _onRedirectCallback = (appState) => {
    const { hash } = window.location;
    let newHistory = appState && appState.redirectOverride ? appState.redirectOverride : appState.targetUrl;
    newHistory = newHistory || window.location.pathname;
    window.history.replaceState({}, document.title, newHistory);

    // eslint-disable-line no-self-assign
    if (hash) {
      window.location.hash = hash;
    }
  };

  const _buildForceTpRedirect = (url = window.location.href) => {
    const splitter = '#';
    const urlParts = url.split(splitter);
    if (urlParts.length !== 2) {
      return window.location.href;
    }
    const newUrlParts = [];
    // eslint-disable-next-line
    newUrlParts[0] = urlParts[0];
    newUrlParts[1] = `${urlParts[0].indexOf('?') > 0 ? '&' : '?'}forcetp=true`;
    newUrlParts[2] = `${splitter}${urlParts[1]}`;

    return newUrlParts.join('');
  };

  const _getDomain = () => {
    let domain = window.location.hostname;
    const hostNameParts = domain.split('.');
    if (hostNameParts.length >= 2) {
      domain = `domain=.${
        hostNameParts[hostNameParts.length - 2]
      }.${
        hostNameParts[hostNameParts.length - 1]}`;
    } else {
      domain = ''; // domain must be removed for localhost
    }
    return domain.toLowerCase();
  };

  const _getExpirationFromDays = (days) => {
    let today = new Date();
    if (days > 0) {
      today.setDate(today.getDate() + days);
    } else {
      today = new Date('January 1, 1970 00:00:01 GMT');
    }
    return today.toUTCString();
  };

  const _getExpirationFromSeconds = (seconds) => {
    let now = new Date();
    if (seconds > 0) {
      now.setSeconds(now.getSeconds() + seconds);
    } else {
      now = new Date('January 1, 1970 00:00:01 GMT');
    }
    return now.toUTCString();
  };

  const _getCookieValue = (a) => {
    const b = document.cookie.match(`(^|;)\\s*${a}\\s*=\\s*([^;]+)`);
    return b ? b.pop() : '';
  };

  const _setAuth0Cookie = (name, val, cookieOptions) => {
    const domain = _getDomain();
    const { expiry } = cookieOptions;

    const auth0Cookie = `${name}=${val}; expires=${expiry}; path=/; ${domain}; Secure; SameSite=None`;
    document.cookie = auth0Cookie;
  };

  const _deleteCookie = (name) => {
    _setAuth0Cookie(name, '', { expiry: _getExpirationFromDays(0) });
  };

  const _setAuth0Authenticated = (authenticated) => {
    _setAuth0Cookie(auth0IsAuthenticatedCookieName, authenticated, { expiry: _getExpirationFromDays(1) });
  };

  const _setAuth0IsLogin = (isLogin) => {
    _setAuth0Cookie(auth0IsLoginCookieName, isLogin, { expiry: _getExpirationFromDays(1) });
  };

  const _setAuth0IsError = (errMsg) => {
    _setAuth0Cookie(auth0IsErrorCookieName, encodeURIComponent(errMsg), { expiry: _getExpirationFromSeconds(15) });
  };

  const _isUrlForV3 = (url) => {
    return !url
      ? false
      : url.match(/^http[s]?:\/\/(ship|v3)/ig);
  };

  const _isUrlForV2 = (url) => {
    return !url
      ? false
      : url.match(/^http[s]?:\/\/(ss|v2)/ig);
  };

  const _isUrlForSsApp = (url) => {
    return _isUrlForV2(url) || _isUrlForV3(url);
  };

  const _isHostnameForV3 = (hostname) => {
    return (
      _urlIndexOf(hostname, 'ship-dev') === 0 || _urlIndexOf(hostname, 'v3-dev') === 0 || _urlIndexOf(hostname, 'ship') === 0
    );
  };

  const _getUrlHostname = (url) => {
    try {
      return new URL(url).hostname;
    } catch (e) {
      console.error(`URL hostname: could not parse ${url}`);
    }
  };

  const _amIinSslocalDomain = () => {
    return _urlIndexOf(window.location.hostname, 'sslocal.com') > 0;
  };

  const _getV3RedirectEquivelantForDev = () => {
    // These redirects are all based on the user's current location
    const currentHostLowerParts = window.location.hostname.split('.');
    currentHostLowerParts[0] = currentHostLowerParts[0]
      .replace(/^ss-dev/i, 'ship-dev')
      .replace(/^v2-dev/i, 'v3-dev')
      .replace(/^dev/i, 'ship-dev')
      .replace(/^ss/i, 'ship');
    return currentHostLowerParts.join('.');
  };

  const _getV2RedirectForZendeskLookup = (partitionHost) => {
    const parts = partitionHost.split('.');
    parts[0] = parts[0].replace(/^ship/i, 'ss');
    return parts.join('.');
  };

  const _specialFilterForDisableRedirectToV3 = (homePartition) => {
    return ssOptions.disableRedirectToV3 && _isHostnameForV3(homePartition) ? window.location.hostname : homePartition;
  };

  const _specialRedirectFilterForDevEnv = (hostname) => {
    // Special redirect behavior in sslocal.com
    if (_amIinSslocalDomain()) {
      return _isHostnameForV3(hostname) ? _getV3RedirectEquivelantForDev() : window.location.hostname;
    }
    return hostname;
  };

  const _specialHomePartitionDevFilters = (homePartition) => {
    if (!homePartition) {
      return window.location.hostname;
    }
    homePartition = _specialFilterForDisableRedirectToV3(homePartition);
    homePartition = _specialRedirectFilterForDevEnv(homePartition);
    return homePartition;
  };

  const _shouldRedirectToUserHome = (user) => {
    return (!_isLocalDevEnv() && !_isSysAdmin(user) && !_isDropin());
  };

  const _isProvidedTargetPathEmpty = (providedTargetUrl) => {
    if (!providedTargetUrl) {
      return true;
    }
    // if the redirect URL is just the site origin (root), it is considered empty
    try {
      const hrefVal = providedTargetUrl.href.replace(/\/+$/, '');
      return hrefVal === providedTargetUrl.origin;
    } catch (err) {
      return true;
    }
  };

  const _isAuth0LoginUrl = (url) => {
    return url.href.match(/^http[s]?:\/\/.+\.com\/login/ig);
  };

  const _getValidTargetUrl = (providedTargetUrl, userPartitionHost) => {
    userPartitionHost = userPartitionHost || '';
    const defaultTargetUrl = window.location.origin;
    providedTargetUrl = providedTargetUrl || defaultTargetUrl;
    const isV3User = _isHostnameForV3(userPartitionHost);

    let url;
    try {
      const derivedTargetUrl = new URL(providedTargetUrl);

      if (!_isDropin() && !_isSysAdmin()
         && _isUrlForSsApp(providedTargetUrl)
         && (
           (isV3User && _isUrlForV2(providedTargetUrl))
          || (!isV3User && _isUrlForV3(providedTargetUrl))
         )
      ) {
        // console.debug(`Incompatible target url for user on parition: ${userPartitionHost}; target: ${derivedTargetUrl}`);
        url = new URL(defaultTargetUrl);
      } else {
        url = _isAuth0LoginUrl(derivedTargetUrl)
          ? new URL(derivedTargetUrl.origin)
          : derivedTargetUrl;
      }

      // If no path is provided, use the app provided default
      url = _isProvidedTargetPathEmpty(url)
        ? new URL(`${url.origin}${ssOptions.defaultRedirectPath || '/'}`)
        : url;

      return url;
    } catch (err) {
      _exception(err);
      return new URL(defaultTargetUrl);
    }
  };

  const _buildRedirectUrlFromTargetUrl = (targetUrl, userPartitionHost) => {
    targetUrl = _getValidTargetUrl(targetUrl, userPartitionHost).href;
    if (!_isUrlForSsApp(targetUrl)) {
      return targetUrl;
    }
    const replaceThisHostname = _getUrlHostname(targetUrl);
    let redirectUrl = _urlReplace(targetUrl, replaceThisHostname, userPartitionHost);
    if (_isUrlForV3(redirectUrl)) {
      redirectUrl = _urlReplace(redirectUrl, '3000', '2999');
    } else {
      redirectUrl = _urlReplace(redirectUrl, '2999', '3000');
    }
    return redirectUrl;
  };

  const _isRequestForTpPublicAsset = () => {
    return (
      window.location.hash && window.location.hash.indexOf('#/public') === 0
    );
  };

  const _exchangeCode = () => {
    return auth0
      .handleRedirectCallback()
      .then((appData) => {
        _onRedirectCallback(appData);
        return appData.appState;
      })
      .then((appState) => {
        return auth0.isAuthenticated().then(isAuthenticated => ({ isAuthenticated, appState }));
        // throw new Error('forced error');
      })
      .catch((e) => {
        _exception(e);
        window.history.replaceState({}, document.title, '/');
        throw e;
      });
  };

  const _overrideRedirectUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('redirect_uri');
  };

  const _getForgotPasswordLink = () => {
    return ssOptions.forgot_password_link
      ? ssOptions.forgot_password_link
      : _buildForceTpRedirect(`${window.location.origin}/?fromAuth0=true#/public/forgot`);
  };

  const _loginWithRedirect = () => {
    const redirectOverride = _overrideRedirectUrl();
    const urlHash = window.location.hash;
    // redirecting to the help center requires a call to v2 node proxy to build a JWT and redirect to zendesk auth.
    const shouldUseZendeskAuth = redirectOverride && redirectOverride.includes('help.shipstation');
    const loginOptions = {
      redirect_uri: ssOptions.redirectAuth0Uri || `${window.location.origin}/login${window.location.hash}`,
      // targetUrl:
      // The app URL that user is attempting to access, it is derived from the current window.location at time of auth flow start
      // redirectOverride:
      // Ignores the current window.location and forces redirect to specified resource (e.g. help center)
      appState: {
        targetUrl: window.location.href,
        redirectOverride,
        shouldUseZendeskAuth,
      },
      database_connection: ssOptions.connection,
      forgot_password_link: _getForgotPasswordLink(),
    };

    const errMsg = _getCookieValue(auth0IsErrorCookieName) || _getCookieValue(authErrorOrLogoutMsg) || undefined;
    if (errMsg) {
      loginOptions.error_msg = errMsg;
      _deleteCookie(auth0IsErrorCookieName);
      _deleteCookie(authErrorOrLogoutMsg);
    }

    _setAuth0IsLogin(!urlHash || urlHash.match(/#\/?$/i));
    auth0.loginWithRedirect(loginOptions);
  };

  const _buildPostLoginSsRedirectUrl = (userData) => {
    // Based on user data from auth0, derive where user should redirect
    if (!userData.user) {
      // console.log('no user found!');
      return;
    }
    console.debug('found authed user =>', JSON.stringify(userData));

    const { redirectOverride, targetUrl } = userData.appState || {};
    // Redirect override? (SS help center, etc)
    if (redirectOverride) {
      // console.debug(`redirect override => ${redirectOverride}`);
      return redirectOverride;
    }

    // Otherwise, derive redirect assuming SS app targetUrl
    let partitionHost = _getUserClaim('partition_host', userData.user);
    partitionHost = _specialHomePartitionDevFilters(partitionHost);
    // console.debug(`actual user redirect host => ${partitionHost}`);

    // 1.) On a partition that is not your "home"? Maybe replace the hostname
    partitionHost = _shouldRedirectToUserHome(userData.user)
      ? partitionHost
      : (_getUrlHostname(targetUrl) || window.location.hostname);
    // console.debug(`derived user redirect host => ${partitionHost}`);

    // 2.) final redirect URL based on the intended target URL
    const redirectUrl = _buildRedirectUrlFromTargetUrl(targetUrl, partitionHost);

    // console.debug(`specified user target url => ${targetUrl}`);
    // console.debug(`derived user target url => ${redirectUrl}`);
    return redirectUrl || window.location.origin;
  };

  /*
  To work around restrictions with
    - auth0
      - Can't have more than one callback url for auth0 (Check me on this)
    - zendesk
      - Similar as above
      - CORS
  We use the existing v2 node proxy endpoint which will generate a JWT and a url to be redirected to for authentication via zendesk

  I think eventually for shipstation (maybe not ship engine) we should be able to use the webv3api endpoint for authenticating with zendesk
  This would decouple things from TP
  */
  const _redirectToThroatpunchZendeskSSO = (userData) => {
    if (!userData.user) {
      return;
    }
    const returnTo = userData.appState.redirectOverride;
    const partitionHost = _getUserClaim('partition_host', userData.user);

    // make sure to hit the legacy ss-node zendesk endpoint
    const legacyPartitionHost = _getV2RedirectForZendeskLookup(partitionHost);

    window.location.href = `https://${legacyPartitionHost}/sso/zendesk/login?return_to=${returnTo}`;
  };

  const _setAccessTokenCookie = (token) => {
    _setAuth0Cookie('_lat', token, { expiry: _getExpirationFromDays(1) });
  };
  const _setAccessTokenCookieV3 = (token) => {
    _setAuth0Cookie('_latv3', token, { expiry: _getExpirationFromDays(1) });
  };

  const _removeAuth0LocalStorage = () => {
    Object.keys(localStorage).every((key) => {
      if (!key.startsWith(auth0LocalStoragePrefix)) return true;
      console.debug(`clean local storage item prior to redirect => ${key}`);

      localStorage.removeItem(key);
      return false;
    });
  };

  const startLogin = () => {
    console.debug(`start login from ${window.location}...`);
    _createClient()
      .then((auth0Client) => {
        // console.debug('auth0 client created...');
        auth0 = auth0Client;
        if (_isCodeExchange()) {
          console.debug('auth0 code exchange...');
          return _exchangeCode();
        }
        // console.debug('call auth0 isAuthenticated...');
        const appState = {};
        const redirectOverride = _overrideRedirectUrl();

        if (redirectOverride) {
          appState.redirectOverride = redirectOverride;
        }
        return auth0.isAuthenticated().then(isAuthenticated => ({ isAuthenticated, appState }));
      })
      .then((authData) => {
        const { isAuthenticated, appState } = authData || {};
        if (isAuthenticated) {
          // console.debug('good auth, get user from auth0...');
          _setAuth0Authenticated('true');
          return auth0.getUser().then(user => ({ user, appState, isAuthenticated }));
        }
        // console.debug('no auth detected, call _loginWithRedirect...');
        _loginWithRedirect();
      })
      .catch((e) => {
        _exception(e);
        // User facing error, no dirty laundry
        _setAuth0IsError('Unable to complete login (Code: 500)');
        auth0.logout({ returnTo: `${window.location.origin}/login` });
      })
      .then((data) => {
        if (!data) return;
        if (!data.appState.shouldUseZendeskAuth) {
          // don't leave a trail of bogus localstorage behind us
          _removeAuth0LocalStorage();
          const redirectUrl = _buildPostLoginSsRedirectUrl(data);
          auth0.getTokenSilently().then((auth0Token) => {
            _setAccessTokenCookieV3(auth0Token);
            window.location.href = redirectUrl;
          });
          return;
        }

        // Otherwise, we're off to Throatpunch to get a ZD jwt
        auth0.getTokenSilently().then((auth0Token) => {
          _setAccessTokenCookie(auth0Token);
          _redirectToThroatpunchZendeskSSO(data);
        });
      })
      .catch((e) => {
        _exception(e);
        document.location.href = '/';
      });
  };

  const handleAuth0Request = () => {
    if (_isRequestForTpPublicAsset()) {
      // prevent Auth0 load, force TP load to get to public asset
      window.location.href = _buildForceTpRedirect();
      return;
    }
    startLogin();
  };

  const createAuth0HelperInternal = (createClient, options) => {
    window.features = {};
    ssOptions = options || {};
    auth0CreateClient = createClient;

    return {
      handleRequest: handleAuth0Request,
      functionsUnderTest: {
        postLoginRedirect: _buildPostLoginSsRedirectUrl,
      },
    };
  };

  return createAuth0HelperInternal;
})();
